import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'language/translator'
import { useTheme } from '@mui/material'

import { cloneDeep } from 'lodash'

import Autocomplete from 'components_new/atoms/Autocomplete'
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Checkbox from 'components_new/atoms/Checkbox'
import Chip from 'components_new/atoms/Chip'
import Collapse from 'components_new/atoms/Collapse'
import FormControl from 'components_new/atoms/FormControl'
import InputLabel from 'components_new/atoms/InputLabel'
import ListItem from 'components_new/atoms/List/ListItem'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import OptionMenuItem from 'components_new/organisms/Widget/WidgetMenu/OptionMenuItem'
import Select from 'components_new/atoms/Select'
import Stack from 'components_new/atoms/Stack'
import TextField from 'components_new/atoms/TextField'
import Icon from 'components_new/atoms/Icon'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'

import type { TAttributeOptionDto } from '@homepal/api-contract'

import { isNumericFilter } from 'utils/functions'

import { FilterOption, FilterOptionsBody } from 'types/GlobalAttributeOptions'
import {
  Condition,
  FilterConditionFormatBody,
  FilterPatchBody,
  FilterType
} from 'types/GlobalKpiOption'
import { translateFilter } from 'utils/enumTranslator'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { useFilterOptions } from 'redux/hooks/FilterOptions'
import {
  formatUpdateFilterBody,
  parseFilterLabelValue,
  parseSingleFilterLabel
} from './utils'

interface NativeFilterGroupSectionProps {
  filterGroupId?: string | null
  filterAttributeOptions: TAttributeOptionDto[]
  filters: FilterConditionFormatBody[]
  updateFilters: (filters: FilterPatchBody[]) => void
}

/** Information:
 * This file is copied and pasted from our native filter group section.
 * It should be re-written och merged with 'NativeFilterSection'.
 */

const NativeFilterGroupSection = (props: NativeFilterGroupSectionProps) => {
  const { filterGroupId, filterAttributeOptions, filters, updateFilters } =
    props
  const theme = useTheme()
  const { t, translateEnum } = useTranslation()

  const filterOptions = useFilterOptions()
  const filterValueOptions = getFilterValueOptions(
    filterAttributeOptions,
    filterOptions
  )
  const filterConditionOptions = filters[0].condition.options

  const [attributeOption, setAttributeOption] =
    useState<TAttributeOptionDto | null>(null)
  const [condition, setCondition] = useState<Condition | null>(null)

  // Value state-handlers
  const [selectedValueOptions, setSelectedValueOptions] = useState<string[]>([])
  const [numericValue, setNumericValue] = useState<number | null>(null)

  const [selectedFilterOptionValue, setSelectedFilterOptionValue] =
    useState<FilterOption | null>(null)
  const [fromValue, setFromValue] = useState<string | null>(null)
  const [toValue, setToValue] = useState<string | null>(null)

  // The filter to pick values from, i.e. attribute_option and condition is set
  const [activeFilter, setActiveFilter] =
    useState<FilterConditionFormatBody | null>(null)

  useEffect(() => {
    if (attributeOption && condition && fromValue && toValue) {
      const tmpActiveFilter =
        filters.find(
          (filter) =>
            filter.attribute_option.selected === attributeOption.id &&
            filter.condition.selected === condition &&
            filter.from_value?.selected === fromValue &&
            filter.to_value?.selected === toValue
        ) || null

      if (tmpActiveFilter) {
        setNumericValue(tmpActiveFilter.value.selected as number)
        setSelectedValueOptions([])
      } else {
        setNumericValue(null)
      }

      setActiveFilter(tmpActiveFilter)
    } else if (
      attributeOption &&
      condition &&
      !isNumericFilter(attributeOption.type)
    ) {
      const tmpActiveFilter =
        filters.find(
          (filter) =>
            filter.attribute_option.selected === attributeOption.id &&
            filter.condition.selected === condition &&
            (!filter.filter_group_id ||
              (filterGroupId && filter.filter_group_id === filterGroupId))
        ) || null

      if (tmpActiveFilter) {
        setNumericValue(null)
      }

      setActiveFilter(tmpActiveFilter)
    }
  }, [attributeOption, condition, fromValue, toValue])

  const onChangeAttributeOption = (attributeOptionId: string | null) => {
    if (!attributeOptionId) {
      setCondition(null)
      setSelectedValueOptions([])
      setNumericValue(null)
      setAttributeOption(null)
      setFromValue(null)
      setToValue(null)
      setSelectedFilterOptionValue(null)
    } else {
      setSelectedFilterOptionValue(filterValueOptions[attributeOptionId])
    }

    const option =
      filterAttributeOptions.find(
        (attributeOption) => attributeOption.id === attributeOptionId
      ) || null

    if (option && !isNumericFilter(option.type)) {
      setFromValue(null)
      setToValue(null)
    }

    if (condition && !validConditionOption(condition, option)) {
      setCondition(null)
    }

    setAttributeOption(option)
  }

  const filterGroups = useMemo(() => {
    const groups: {
      [groupId: string]: {
        filterGroupId?: string | null
        filters: FilterConditionFormatBody[]
      }
    } = {}

    filters
      .filter((filter) => filter.condition.selected)
      .forEach((filter, i) => {
        const group = filter.filter_group_id || i

        if (group in groups) {
          groups[group].filters.push(filter)
        } else {
          groups[group] = {
            filterGroupId: filter.filter_group_id,
            filters: [filter]
          }
        }
      })

    return Object.values(groups).sort((a, b) =>
      sortAlphabeticalAsc(
        a.filters[0].attribute_option.selected,
        b.filters[0].attribute_option.selected
      )
    )
  }, [filters])

  return (
    <Box sx={{ mb: 2 }}>
      <ListItem>
        <FormControl fullWidth size="small">
          <InputLabel id="left-attribute-label">
            {t('filters', 'filter_on')}
          </InputLabel>
          <Select
            labelId="left-attribute-label"
            label={t('filters', 'filter_on')}
            value={attributeOption ? attributeOption.id : ''}
          >
            <MenuItem value={''} onClick={() => onChangeAttributeOption(null)}>
              <em>{t('global', 'none')}</em>
            </MenuItem>
            {filterAttributeOptions.map((option) => (
              <OptionMenuItem
                value={option.id}
                disabled={option.disabled}
                name={option.name || t('filters', 'is_empty')}
                key={option.id}
                onClick={() => onChangeAttributeOption(option.id)}
                selected={
                  attributeOption ? attributeOption.id === option.id : false
                }
              >
                {option.name}
              </OptionMenuItem>
            ))}
          </Select>
        </FormControl>
      </ListItem>
      <Collapse
        in={
          !!(
            selectedFilterOptionValue &&
            selectedFilterOptionValue.type === 'FROM_TO'
          )
        }
      >
        <ListItem>
          <Stack>
            <FormControl fullWidth size="small" sx={{ minWidth: 125 }}>
              <InputLabel id="from-value-label">
                {t('global', 'from')}
              </InputLabel>
              <Select
                labelId="from-value-label"
                label={t('global', 'from')}
                value={fromValue ?? ''}
                onChange={(event) => {
                  setFromValue(event.target.value)
                }}
              >
                <MenuItem value="">
                  <em>{t('global', 'none')}</em>
                </MenuItem>
                {selectedFilterOptionValue?.from_values?.map((option, i) => (
                  <MenuItem key={i} value={option}>
                    {option}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" sx={{ minWidth: 125 }}>
              <InputLabel id="to-value-label">{t('global', 'to')}</InputLabel>
              <Select
                labelId="to-value-label"
                label={t('global', 'to')}
                value={toValue ?? ''}
                onChange={(event) => {
                  setToValue(event.target.value)
                }}
              >
                <MenuItem value="">
                  <em>{t('global', 'none')}</em>
                </MenuItem>
                {selectedFilterOptionValue?.to_values?.map((option, i) => (
                  <MenuItem key={i} value={option}>
                    {option}
                  </MenuItem>
                )) || []}
              </Select>
            </FormControl>
          </Stack>
        </ListItem>
      </Collapse>
      <Collapse in={Boolean(attributeOption)}>
        <ListItem>
          <FormControl fullWidth size="small">
            <InputLabel id="condition-label">
              {t('filters', 'select_condition')}
            </InputLabel>
            <Select
              labelId="condition-label"
              label={t('filters', 'select_condition')}
              value={condition ?? ''}
              onChange={(event) => {
                if (
                  !event.target.value ||
                  onlyLeftCondition(event.target.value as Condition)
                ) {
                  setSelectedValueOptions([])
                  setNumericValue(null)
                }

                setCondition(event.target.value as Condition)
              }}
            >
              <MenuItem value="">
                <em>{t('global', 'none')}</em>
              </MenuItem>
              {filterConditionOptions
                .filter((option) =>
                  validConditionOption(option, attributeOption)
                )
                .map((option, i) => (
                  <MenuItem key={i} value={option}>
                    {translateEnum('conditions', option)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </ListItem>
      </Collapse>
      <Collapse
        in={
          Boolean(attributeOption) &&
          Boolean(condition) &&
          !onlyLeftCondition(condition)
        }
      >
        <ListItem>
          <FormControl fullWidth size="small">
            <FilterValueInput
              filterValueOptions={filterValueOptions}
              activeFilter={activeFilter}
              selectedValueOptions={selectedValueOptions}
              setSelectedValueOptions={setSelectedValueOptions}
              numericValue={numericValue}
              setNumericValue={setNumericValue}
              attributeOption={attributeOption}
            />
          </FormControl>
        </ListItem>
      </Collapse>
      <Collapse in={Boolean(attributeOption) && Boolean(condition)}>
        <ListItem>
          <Button
            variant="contained"
            fullWidth={true}
            onClick={() => {
              if (
                attributeOption &&
                validFilter(
                  attributeOption.id,
                  condition,
                  selectedValueOptions.length === 0
                    ? numericValue
                    : selectedValueOptions
                )
              ) {
                let newFilters: FilterPatchBody[] = []
                const numericValueIsSet = !!(numericValue || numericValue === 0)

                if (onlyLeftCondition(condition)) {
                  newFilters = [
                    {
                      attribute_option_id: attributeOption.id,
                      condition: condition as Condition,
                      value: null,
                      type: FilterType.ONLY_LEFT
                    }
                  ]
                } else if (selectedValueOptions.length > 0) {
                  newFilters = selectedValueOptions.map((value) => ({
                    attribute_option_id: attributeOption.id,
                    condition: condition as Condition,
                    value: parseFilterLabelValue(value, t),
                    type: FilterType.INPUT_VALUE
                  }))
                } else if (
                  numericValueIsSet &&
                  activeFilter?.attribute_option.selected !==
                    attributeOption.id &&
                  fromValue &&
                  toValue
                ) {
                  newFilters = [
                    {
                      attribute_option_id: attributeOption.id,
                      condition: condition as Condition,
                      value: numericValue,
                      type: FilterType.FROM_TO,
                      from_value: fromValue as string,
                      to_value: toValue as string
                    }
                  ]
                } else if (numericValueIsSet) {
                  newFilters = [
                    {
                      attribute_option_id: attributeOption.id,
                      condition: condition as Condition,
                      value: numericValue,
                      type: FilterType.NUMBER
                    }
                  ]
                }

                // If no filter it set, only set the new filter
                if (
                  filters.length === 1 &&
                  !validFilter(
                    filters[0].attribute_option.selected,
                    filters[0].condition.selected,
                    filters[0].value.selected
                  )
                ) {
                  updateFilters(newFilters)
                } else {
                  /**
                   * If there is a current filter, format saved body to
                   * a valid format for the filter update
                   */
                  updateFilters([
                    ...formatUpdateFilterBody(
                      filters,
                      attributeOption.id,
                      condition,
                      selectedValueOptions,
                      numericValue,
                      fromValue,
                      toValue
                    ),
                    ...newFilters
                  ])
                }

                setCondition(null)
                setSelectedValueOptions([])
                setNumericValue(null)
                setAttributeOption(null)
                setFromValue(null)
                setToValue(null)
                setActiveFilter(null)
                setSelectedFilterOptionValue(null)
              }
            }}
          >
            Lägg till
          </Button>
        </ListItem>
      </Collapse>

      {filterGroups.length === 0 ? (
        <ListItem>
          <EmptyPlaceholder
            background={true}
            sx={{ width: '100%' }}
            title={t('filters', 'no_active_filters')}
          />
        </ListItem>
      ) : (
        filterGroups.map((group, i) => {
          const groupFilters = group.filters
          const isGrouped = !!group.filterGroupId && groupFilters.length > 1

          const chips = [...groupFilters]
            .sort((a, b) =>
              sortAlphabeticalAsc(
                a.attribute_option.selected,
                b.attribute_option.selected
              )
            )
            .map((filter, j) => {
              const filterAttribute = filterAttributeOptions.find(
                (option) => filter.attribute_option.selected === option.id
              )

              let value: string | number = ''

              if (
                filter.value.selected &&
                typeof filter.value.selected === 'object' &&
                filter.value.selected.length > 0
              ) {
                value = filter.value.selected.join(', ')
              } else if (
                filter.value.selected &&
                typeof filter.value.selected === 'number'
              ) {
                value = filter.value.selected
              }

              return (
                <>
                  <Chip
                    key={j}
                    label={translateFilter(
                      filterAttribute ? filterAttribute.name : '',
                      filter.condition.selected as Condition,
                      value as string,
                      t,
                      translateEnum,
                      filter.from_value?.selected,
                      filter.to_value?.selected
                    )}
                    size="small"
                    sx={{
                      width: 'fit-content',
                      justifyContent: 'flex-start',
                      my: 0.25
                    }}
                    onDelete={
                      isGrouped
                        ? undefined
                        : () => {
                            const newFilters = cloneDeep(filters).filter(
                              (f) =>
                                f.attribute_option.selected !==
                                  filter.attribute_option.selected ||
                                f.condition.selected !==
                                  filter.condition.selected ||
                                f.type.selected !== filter.type.selected ||
                                f.filter_group_id !== filter.filter_group_id
                            )

                            updateFilters(formatUpdateFilterBody(newFilters))
                          }
                    }
                  />
                </>
              )
            })

          return (
            <>
              <ListItem key={`filter-${i}`}>
                {isGrouped ? (
                  <Box
                    sx={{
                      border: '1px dashed black',
                      padding: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      width: '100%'
                    }}
                  >
                    <Box
                      onClick={() => {
                        const newFilters = cloneDeep(filters).filter(
                          (f) => f.filter_group_id !== group.filterGroupId
                        )

                        updateFilters(formatUpdateFilterBody(newFilters))
                      }}
                    >
                      <Icon
                        name={'Cancel'}
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          fontSize: 16,
                          color: theme.palette.primary.transparentDark,
                          cursor: 'pointer'
                        }}
                      />
                    </Box>
                    {chips}
                  </Box>
                ) : (
                  chips
                )}
              </ListItem>
            </>
          )
        })
      )}
    </Box>
  )
}

export const FilterValueInput: FC<{
  filterValueOptions: { [attribute_option_id: string]: FilterOption }
  activeFilter: FilterConditionFormatBody | null
  selectedValueOptions: string[] | number
  setSelectedValueOptions: (options: string[]) => void
  numericValue: number | null
  setNumericValue: (option: number) => void
  attributeOption: TAttributeOptionDto | null
}> = ({
  filterValueOptions,
  activeFilter,
  selectedValueOptions,
  setSelectedValueOptions,
  numericValue,
  setNumericValue,
  attributeOption
}) => {
  const { t } = useTranslation()
  const sx = {
    minWidth: 200,
    '& .MuiFormLabel-root': {
      // avoid label under clear icon.
      maxWidth: 'calc(100% - 72px)'
    },
    '& .MuiFormControl-root': {
      margin: 0
    }
  }

  const options = cloneDeep(
    (attributeOption && filterValueOptions[attributeOption.id]?.values) || []
  ).sort(sortAlphabeticalAsc)

  return isNumericFilter(attributeOption?.type) ? (
    <TextField
      type="number"
      label={t('filters', 'value')}
      InputProps={{
        inputMode: 'numeric',
        pattern: '[0-9]*'
      }}
      onChange={(event) => setNumericValue(parseInt(event.target.value))}
      size="small"
      value={numericValue ?? ''}
      sx={{ ...sx, my: 0 }}
    />
  ) : (
    <Autocomplete
      multiple
      options={options
        .map((opt) => parseSingleFilterLabel(opt, t))
        .filter((option) =>
          activeFilter &&
          activeFilter.value.selected &&
          typeof activeFilter.value.selected !== 'number' &&
          activeFilter.value.selected?.length > 0
            ? !activeFilter.value.selected?.includes(option)
            : true
        )}
      getOptionLabel={(opt) => opt}
      value={selectedValueOptions}
      onChange={(event, options) => {
        setSelectedValueOptions(options)
      }}
      isOptionEqualToValue={(option, value) => option === value}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('filters', 'select_x', false, {
            name: attributeOption ? attributeOption.name.toLowerCase() : ''
          })}
          placeholder={t('global', 'search')}
        />
      )}
      disableCloseOnSelect
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option}>
          <Checkbox sx={{ mr: 1 }} checked={selected} />
          {option}
        </li>
      )}
      size={'small'}
      sx={sx}
    />
  )
}

const getFilterValueOptions = (
  filterAttributeOptions: TAttributeOptionDto[],
  filterOptions: FilterOptionsBody | null
) => {
  const filterValueOptions: { [key: string]: FilterOption } = {}

  if (filterOptions) {
    filterAttributeOptions.forEach((option) => {
      const options = filterOptions[option.id]

      filterValueOptions[option.id] = options
    })
  }

  return filterValueOptions
}

const onlyLeftCondition = (condition: Condition | null) =>
  condition && [Condition.IS_NULL, Condition.IS_NOT_NULL].includes(condition)

const validConditionOption = (
  condition: Condition,
  attributeOption: TAttributeOptionDto | null
) => {
  if (attributeOption && isNumericFilter(attributeOption.type)) {
    return [Condition.LT, Condition.GT].includes(condition)
  } else if (attributeOption) {
    return [
      Condition.EQ,
      Condition.NE,
      Condition.IS_NULL,
      Condition.IS_NOT_NULL
    ].includes(condition)
  }

  return false
}

export const validFilter = (
  attributeOption: string | null,
  condition: Condition | null,
  values: number | (string | null)[] | null
) => {
  if (attributeOption && condition && onlyLeftCondition(condition)) {
    return true
  } else if (
    attributeOption &&
    condition &&
    typeof values === 'number' &&
    values !== null &&
    !isNaN(values)
  ) {
    return true
  } else if (
    attributeOption &&
    condition &&
    values &&
    (values as (string | null)[])?.length > 0
  ) {
    return true
  }

  return false
}

export default NativeFilterGroupSection
